import { bottomModalPortalContainerId } from "client/config/divIds";
import type { Height } from "client/types/Height";
import type { HTMLAttributes, ReactNode, UIEvent } from "react";
import { createPortal } from "react-dom";
import { useBottomModal } from "./useBottomModal";
import { stopPropagation } from "client/utils/stopPropagation";
import { twMerge } from "tailwind-merge";

export interface BottomModalProps {
    isOpen: boolean;
    children: ReactNode;
    lockBackground?: boolean;
    parentClassNames?: HTMLAttributes<HTMLDivElement>["className"];
    contentParentClassNames?: HTMLAttributes<HTMLDivElement>["className"];
    initialHeight?: Height;
    minimumHeight?: number;
    interactiveBackground?: boolean;
    titleBar?: ReactNode;
    bottomSection?: ReactNode;
    onClose?: () => void;
    disableDrag?: boolean;
    containerId?: string;
    handleScroll?: (e: UIEvent) => void;
}

export const BOTTOM_MODAL_SHADOW = `0px -10px 30px rgba(0, 0, 0, 0.1)`;

export const BottomModal = ({
    isOpen,
    children,
    onClose,
    interactiveBackground = false,
    lockBackground = true,
    titleBar,
    bottomSection,
    initialHeight = "45vh",
    minimumHeight,
    parentClassNames,
    contentParentClassNames,
    disableDrag,
    containerId,
    handleScroll,
}: BottomModalProps) => {
    const {
        childRef,
        handleClose,
        handleTouchEnd,
        handleTouchMove,
        handleTouchStart,
        isMounted,
        contentRef,
        bottomSectionRef,
    } = useBottomModal(isOpen, lockBackground, minimumHeight, onClose);

    const element = typeof window === "undefined" ? null : document.getElementById(bottomModalPortalContainerId);

    if (!isOpen || !isMounted || !element) return null;

    return createPortal(
        <div
            className={twMerge(
                `text-black fixed grid inset-0 z-40 bg-gray-500 bg-opacity-10 lg:hidden ${
                    interactiveBackground && `pointer-events-none`
                } overflow-hidden`,
                parentClassNames,
            )}
            onClick={handleClose}
            id={containerId}
        >
            <div
                className={twMerge(
                    `relative overflow-y-auto bg-white dark:bg-darkSurface-100 mt-auto z-50 rounded-t-2xl pointer-events-auto h-full`,
                    contentParentClassNames,
                )}
                style={{
                    transform: `translateY(calc(100vh - ${initialHeight}))`,
                    boxShadow: BOTTOM_MODAL_SHADOW,
                }}
                onClick={stopPropagation()}
                ref={childRef}
                draggable={true}
            >
                {!disableDrag && (
                    <div
                        className={`sticky top-0 z-50 w-full flex flex-col items-center touch-none`}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="bg-irGray-400 dark:bg-darkSurface-600 mt-2"
                            style={{
                                width: "40px",
                                height: "6px",
                                borderRadius: "3px",
                            }}
                        />
                        <div className="w-full">{titleBar}</div>
                    </div>
                )}
                {disableDrag && <div className="w-full">{titleBar}</div>}
                <div
                    className="overflow-y-auto h-full"
                    onClick={stopPropagation()}
                    ref={contentRef}
                    onScroll={handleScroll}
                >
                    {children}
                </div>
            </div>
            {bottomSection && (
                <div
                    className="fixed left-0 right-0 bottom-0 z-50 pointer-events-auto"
                    ref={bottomSectionRef}
                    onClick={stopPropagation()}
                >
                    {bottomSection}
                </div>
            )}
        </div>,
        element,
    );
};
